// errorTypes.ts
import type { ErrorResponse } from '@/schemas/errorResponseSchema';

// strip out auth token
const SENSITIVE_HEADER_NAMES = new Set(['ACCESS_TOKEN']);

function headersToObject(headers: Headers) {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => {
    const k = key.toLowerCase();
    if (SENSITIVE_HEADER_NAMES.has(k)) return;
    out[k] = value;
  });
  return out;
}

export class ApiError extends Error {
  name = 'ApiError';

  method: string;
  url: string;
  path: string;
  status: number;
  statusText: string;

  // Snapshots for debugging
  requestHeaders: Record<string, string>;
  responseHeaders: Record<string, string>;

  // Body info
  errorData?: ErrorResponse;

  constructor(
    public readonly request: Request,
    public readonly response: Response,
    errorData?: ErrorResponse
  ) {
    const url = new URL(request.url);
    const status = response.status;

    const msg = `${status} ${response.statusText} ${request.method} ${url.pathname}`;

    super(msg);

    this.method = request.method;
    this.url = url.toString();
    this.path = url.pathname;
    this.status = status;
    this.statusText = response.statusText;

    this.requestHeaders = headersToObject(request.headers);
    this.responseHeaders = headersToObject(response.headers);

    this.errorData = errorData;
  }

  /** Short, queryable labels */
  toSentryTags() {
    return {
      http_status: String(this.status),
      http_method: this.method,
      http_path: this.path,
    } as Record<string, string | undefined>;
  }

  /** Rich, non-indexed payloads */
  toSentryExtras() {
    return {
      url: this.url,
      statusText: this.statusText,
      requestHeaders: this.requestHeaders,
      responseHeaders: this.responseHeaders,
      errorId: this.errorData?.error_id,
      errorMessage: this.errorData?.error_message,
    };
  }
}
